/**
 * Persistent player profile — cross-game XP + mastery rank.
 *
 * Stored entirely in localStorage under `aether.profile`. No server
 * sync (yet). Each game calls recordRun() at game-over with its score;
 * we convert to XP via a sub-linear curve so a 10× higher score
 * doesn't crowd out other games' contributions.
 *
 * Schema version is pinned at 1 so future migrations can be additive.
 */

export const PROFILE_KEY = "aether.profile";

export type GameId =
  | "drift" | "pulse" | "bulwark" | "echo" | "spire" | "quickdraw"
  | "foraging" | "ascent" | "lattice" | "sigil" | "resonance"
  | "frequency" | "hailstorm" | "throttle" | "slipstream" | "vortex" | "maw" | "splinter" | "phalanx";

export interface Profile {
  schemaVersion: 1;
  totalXp: number;
  gameXp: Record<string, number>;
  gameBest: Record<string, number>;
  gameRuns: Record<string, number>;
  /** ISO date strings — one entry per day a run was played. */
  daysPlayed: string[];
  lastPlayedAt?: string;
}

function emptyProfile(): Profile {
  return {
    schemaVersion: 1,
    totalXp: 0,
    gameXp: {},
    gameBest: {},
    gameRuns: {},
    daysPlayed: [],
  };
}

export function getProfile(): Profile {
  try {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return emptyProfile();
    const p = JSON.parse(raw) as Profile;
    if (p.schemaVersion !== 1) return emptyProfile();
    // Defensive — older entries may lack fields after a version bump.
    p.gameXp = p.gameXp ?? {};
    p.gameBest = p.gameBest ?? {};
    p.gameRuns = p.gameRuns ?? {};
    p.daysPlayed = p.daysPlayed ?? [];
    return p;
  } catch {
    return emptyProfile();
  }
}

function saveProfile(p: Profile) {
  try { localStorage.setItem(PROFILE_KEY, JSON.stringify(p)); }
  catch { /* private mode / quota — silently lose the update */ }
}

/** XP from a single run. Sub-linear so big scores don't dwarf small ones. */
export function xpFromScore(score: number): number {
  if (!Number.isFinite(score) || score <= 0) return 0;
  return Math.floor(Math.sqrt(score) * 2);
}

interface RankTier { name: string; minXp: number }
const RANK_THRESHOLDS: readonly RankTier[] = [
  { name: "Novice",      minXp: 0 },
  { name: "Adept",       minXp: 500 },
  { name: "Skilled",     minXp: 2000 },
  { name: "Expert",      minXp: 6000 },
  { name: "Master",      minXp: 15_000 },
  { name: "Grandmaster", minXp: 40_000 },
];

export function rankForXp(xp: number): { name: string; nextAt?: number } {
  let current: RankTier = RANK_THRESHOLDS[0];
  for (let i = 0; i < RANK_THRESHOLDS.length; i++) {
    if (xp >= RANK_THRESHOLDS[i].minXp) current = RANK_THRESHOLDS[i];
  }
  const next = RANK_THRESHOLDS.find((r) => r.minXp > xp);
  return { name: current.name, nextAt: next?.minXp };
}

export interface RunRecord {
  game: GameId;
  score: number;
}

/** Record a finished run. Returns the XP earned + the new total/rank.
 *  Also fires any newly-earned cross-game achievement toasts. */
export function recordRun(rec: RunRecord): { earnedXp: number; totalXp: number; rank: string; newBest: boolean } {
  const p = getProfile();
  const earnedXp = xpFromScore(rec.score);
  p.totalXp += earnedXp;
  p.gameXp[rec.game] = (p.gameXp[rec.game] ?? 0) + earnedXp;
  p.gameRuns[rec.game] = (p.gameRuns[rec.game] ?? 0) + 1;
  const prevBest = p.gameBest[rec.game] ?? 0;
  const newBest = rec.score > prevBest;
  if (newBest) p.gameBest[rec.game] = rec.score;
  const today = new Date().toISOString().slice(0, 10);
  if (!p.daysPlayed.includes(today)) p.daysPlayed.push(today);
  p.lastPlayedAt = new Date().toISOString();
  saveProfile(p);
  const rank = rankForXp(p.totalXp).name;
  // Fire achievement toasts for any newly-earned cross-game achievements.
  // Late import to avoid a circular dependency at module init.
  setTimeout(() => {
    import("./achievements").then((m) => m._checkAndShowToasts()).catch(() => {});
  }, 600);
  // Spectator bar — initialize once and update after each run. Both calls
  // are no-ops when ?spectator=1 isn't in the URL.
  import("./spectator").then((m) => {
    m.initSpectator(rec.game);
    m.recordSpectatorRun(rec.game, rec.score);
  }).catch(() => {});
  return { earnedXp, totalXp: p.totalXp, rank, newBest };
}

/** Show a brief XP toast on the page. Idempotent if called repeatedly. */
export function showXpToast(earnedXp: number, totalXp: number, rank: string) {
  if (earnedXp <= 0) return;
  const el = document.createElement("div");
  el.className = "xp-toast";
  el.setAttribute("role", "status");
  el.setAttribute("aria-live", "polite");
  el.innerHTML = `<span class="xp-amount">+${earnedXp} XP</span>
    <span class="xp-meta">${totalXp.toLocaleString()} total · ${rank}</span>`;
  document.body.appendChild(el);
  requestAnimationFrame(() => el.classList.add("in"));
  window.setTimeout(() => {
    el.classList.remove("in");
    el.classList.add("out");
    window.setTimeout(() => el.remove(), 400);
  }, 2400);
}
